import {
  WebGLRenderer,
  ACESFilmicToneMapping,
  sRGBEncoding,
  AdditiveBlending
} from 'three'

import {
  EffectComposer,
  RenderPass,
  EffectPass,
  DepthOfFieldEffect,
  SSAOEffect,
  BloomEffect,
  SelectiveBloomEffect,
  SMAAEffect,
  SMAAPreset,
  NormalPass,
  BlendFunction,
  KernelSize,
  EdgeDetectionMode
} from 'postprocessing'

// import { gui, guiObj } from '@/utils/gui'

export class Renderer {
  constructor (scene, camera, preserveDrawingBuffer = false, transparent = false) {
    this.scene = scene
    this.camera = camera

    this.camera.layers.enable( 0 )
    this.camera.layers.enable( 1 )

    this.resize = this.resize.bind(this)
    this.render = this.render.bind(this)

    this.webGLRenderer = new WebGLRenderer({
      antialias: false,
      alpha: transparent,
      stencil: false,
      depth: false,
      preserveDrawingBuffer: preserveDrawingBuffer
    })

    // this.webGLRenderer.toneMappingExposure = 1
    this.webGLRenderer.setClearColor(0x111111, transparent ? 0 : 1)
    this.webGLRenderer.outputEncoding = sRGBEncoding
    this.webGLRenderer.toneMapping = ACESFilmicToneMapping
    this.webGLRenderer.gammaFactor = 2.2
    this.webGLRenderer.localClippingEnabled = true
    this.webGLRenderer.shadowMap.enabled = true

    this.bloomEffect = new BloomEffect({
      intensity: .7,
      resolutionScale: .35,
      luminanceThreshold: .3,
      luminanceSmoothing: .2,
      kernelSize: KernelSize.MEDIUM
    })
    this.bloomEffect.blurPass.scale = .35

    this.selectiveBloomEffect = new SelectiveBloomEffect(this.scene, this.camera, {
      blendFunction: BlendFunction.SCREEN,
      intensity: 2,
      luminanceThreshold: .2,
      luminanceSmoothing: .03,
      kernelSize: KernelSize.HUGE
    })
    // this.selectiveBloomEffect.setSize(512, 512)
    // this.selectiveBloomEffect.setSize(2048, 2048)
    this.selectiveBloomEffect.blurPass.scale = .02
    this.selectiveBloomEffect.selection.layer = 1

    this.smaaEffect = new SMAAEffect(
      Object.assign(new Image(), { src: SMAAEffect.searchImageDataURL }),
      Object.assign(new Image(), { src: SMAAEffect.areaImageDataURL }),
      SMAAPreset.MEDIUM,
      EdgeDetectionMode.DEPTH
    )

    this.bokehEffect = new DepthOfFieldEffect(this.camera, {
      bokehScale: 3.3,
      focusDistance: .01,
      focalLength: .06
    })

    if (!transparent) {
      this.effectPass1 = new EffectPass(this.camera,
        this.bloomEffect,
        this.bokehEffect,
        this.smaaEffect
      )
    }
    else {
      this.effectPass1 = new EffectPass(this.camera,
        this.bloomEffect,
        this.smaaEffect
      )
    }

    this.effectPass1.renderToScreen = false
    const material = this.effectPass1.getFullscreenMaterial()

    this.renderPass = new RenderPass(this.scene, this.camera)

    this.effectPass2 = new EffectPass(this.camera, this.selectiveBloomEffect)
    this.effectPass2.renderToScreen = true
    this.effectPass2.enabled = true

    this.composer = new EffectComposer(this.webGLRenderer)
    this.composer.addPass(this.renderPass)
    if (!preserveDrawingBuffer) {
      this.composer.addPass(this.effectPass1)
      this.composer.addPass(this.effectPass2)
    }

    // this.addGui()
  }

  get canvas () {
    return this.webGLRenderer.domElement
  }

  resize ({width, height}) {
    this.webGLRenderer.setSize(width, height)
    this.composer.setSize(width, height)

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
  }

  render () {
    this.composer.render()
  }

  destroy () {
    // this.renderPass.dispose()
    // this.effectPass1.dispose()
    // this.effectPass2.dispose()
    // this.composer.dispose()
  }

  addGui () {
    const guiBloom = gui.addFolder('Bloom')
    guiBloom.add(this.bloomEffect, 'intensity').min(0).max(5).step(0.01)
    guiBloom.add(this.bloomEffect.blurPass, 'scale').min(0).max(5).step(0.01)
    guiBloom.add(this.bloomEffect.luminanceMaterial, 'threshold').min(0).max(1).step(0.01)
    guiBloom.add(this.bloomEffect.luminanceMaterial, 'smoothing').min(0).max(1).step(0.01)

    const guiBloomSel = gui.addFolder('SelectiveBloom')
    guiBloomSel.add(this.selectiveBloomEffect, 'intensity').min(0).max(5).step(0.01)
    guiBloomSel.add(this.selectiveBloomEffect.blurPass, 'scale').min(0).max(1).step(0.01)
    guiBloomSel.add(this.selectiveBloomEffect.luminanceMaterial, 'threshold').min(0).max(1).step(0.01)
    guiBloomSel.add(this.selectiveBloomEffect.luminanceMaterial, 'smoothing').min(0).max(1).step(0.01)

    const guiBokeh = gui.addFolder('Bokeh')
    guiBokeh.add(this.bokehEffect, 'bokehScale').min(0).max(5).step(0.001)
    guiBokeh.add(this.bokehEffect.circleOfConfusionMaterial.uniforms.focusDistance, 'value').min(0).max(.1).step(0.0001)
    guiBokeh.add(this.bokehEffect.circleOfConfusionMaterial.uniforms.focalLength, 'value').min(0).max(.2).step(0.0001)

    const guiSMAA = gui.addFolder('SMAA')
    const guiplop = {
      width: 1,
      height: 1
    }
    guiSMAA.add(guiplop, 'width').min(0).max(4096).step(1).onChange(()=>{
      this.smaaEffect.setSize(guiplop.width, guiplop.width)
    })
  }

  set bokeh (value) {
    this.bokehEffect.circleOfConfusionMaterial.uniforms.focalLength.value = value * .06
  }

  get bokeh () {
    return this.bokehEffect.circleOfConfusionMaterial.uniforms.focalLength.value / .06
  }
}
